import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { loginUser, handleGoogleLogin } from '../lib/auth';
import { supabase } from '../lib/supabase';
import { startGoogleLogin } from '../features/auth/startGoogleLogin';
import toast from 'react-hot-toast';
import { FaBook, FaLock, FaEnvelope, FaStar, FaGoogle } from 'react-icons/fa';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showExistingAccountModal, setShowExistingAccountModal] = useState(false);
  const [iconPosition, setIconPosition] = useState({ x: 0, y: 0 });
  const [iconRotation, setIconRotation] = useState(0);
  const navigate = useNavigate();

  // Animate the icon position and rotation
  useEffect(() => {
    const interval = setInterval(() => {
      setIconPosition({
        x: (Math.random() * 10) - 5, // Move -5px to +5px horizontally
        y: (Math.random() * 10) - 5  // Move -5px to +5px vertically
      });
      setIconRotation(Math.random() * 20 - 10); // Rotate -10deg to +10deg
    }, 2000);
    
    return () => clearInterval(interval);
  }, []);

  // Check for Google auth redirect
  useEffect(() => {
    const checkGoogleAuth = async () => {
      console.log('Checking for Google auth session');
      try {
        const { data } = await supabase.auth.getSession();
        console.log('Auth session data:', data);
        if (data.session) {
          console.log('Session found, processing Google login');
          setGoogleLoading(true);
          const user = await handleGoogleLogin();
          if (user) {
            console.log('Google login successful, redirecting to dashboard');
            toast.success('Login successful!');
            navigate('/dashboard');
          }
        }
      } catch (error) {
        console.error('Error checking Google auth:', error);
        toast.error('Google login failed. Please try again.');
      } finally {
        setGoogleLoading(false);
      }
    };
    
    checkGoogleAuth();
  }, [navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await loginUser(email, password);
      localStorage.setItem('user', JSON.stringify(user));
      sessionStorage.setItem('user', JSON.stringify(user));
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setGoogleLoading(true);
      await startGoogleLogin();
      // Redirect is handled by Supabase
    } catch (error: any) {
      toast.error(error.message || 'Google sign in failed');
      setGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Starry Night Sky Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Stars */}
        {[...Array(300)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-white rounded-full animate-twinkle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${Math.random() * 4 + 1}px`,
              height: `${Math.random() * 4 + 1}px`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${2 + Math.random() * 4}s`
            }}
          ></div>
        ))}
        
        {/* Shooting Stars */}
        {[...Array(5)].map((_, i) => (
          <div
            key={`shooting-${i}`}
            className="absolute w-1 h-1 bg-yellow-400 rounded-full animate-shooting-star"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 50}%`,
              animationDelay: `${Math.random() * 10}s`,
              animationDuration: '3s'
            }}
          ></div>
        ))}

        {/* Moon */}
        <div className="absolute top-10 right-10 w-20 h-20 bg-yellow-100 rounded-full shadow-lg animate-pulse opacity-80"></div>
        
        {/* Constellation Lines */}
        <svg className="absolute inset-0 w-full h-full opacity-30">
          <defs>
            <pattern id="constellation" x="0" y="0" width="200" height="200" patternUnits="userSpaceOnUse">
              <line x1="50" y1="50" x2="150" y2="100" stroke="white" strokeWidth="0.5" opacity="0.3"/>
              <line x1="150" y1="100" x2="100" y2="150" stroke="white" strokeWidth="0.5" opacity="0.3"/>
              <circle cx="50" cy="50" r="1" fill="white"/>
              <circle cx="150" cy="100" r="1" fill="white"/>
              <circle cx="100" cy="150" r="1" fill="white"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#constellation)"/>
        </svg>
      </div>

      <div className="max-w-md w-full space-y-8 relative z-10">
        {/* Logo and Title */}
        <div className="text-center">
          <div className="w-16 h-16 mx-auto bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center shadow-lg transform hover:scale-105 transition-all duration-300"
               style={{
                 transform: `translate(${iconPosition.x}px, ${iconPosition.y}px) rotate(${iconRotation}deg) scale(1)`,
                 transition: 'transform 1s ease-in-out'
               }}>
            <FaBook className="w-8 h-8 text-black animate-pulse" />
          </div>
          <h2 className="mt-6 text-4xl font-extrabold text-white">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 to-yellow-200">
              Welcome Back
            </span>
          </h2>
          <p className="mt-2 text-sm text-white/70 animate-pulse">
            Sign in to continue your language learning journey
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl p-8 space-y-6 border border-white/20 transform hover:scale-[1.01] transition-all duration-300">
          <form className="space-y-6" onSubmit={handleLogin}>
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Email Address
              </label>
              <div className="mt-1 relative group">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaEnvelope className="h-5 w-5 text-white/60 group-hover:text-yellow-400 transition-colors duration-200" />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none block w-full pl-10 pr-3 py-3 border border-white/30 rounded-xl bg-white/20 backdrop-blur-lg placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent sm:text-sm transition-colors duration-200 text-white"
                  placeholder="Enter your email"
                  required
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center opacity-0 group-focus-within:opacity-100 transition-opacity">
                  <FaStar className="h-4 w-4 text-yellow-400 animate-pulse" />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Password
              </label>
              <div className="mt-1 relative group">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaLock className="h-5 w-5 text-white/60 group-hover:text-yellow-400 transition-colors duration-200" />
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full pl-10 pr-3 py-3 border border-white/30 rounded-xl bg-white/20 backdrop-blur-lg placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent sm:text-sm transition-colors duration-200 text-white"
                  placeholder="Enter your password"
                  required
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center opacity-0 group-focus-within:opacity-100 transition-opacity">
                  <FaStar className="h-4 w-4 text-yellow-400 animate-pulse" />
                </div>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-black bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:-translate-y-1"
              >
                <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                  <FaLock className="h-5 w-5 text-yellow-800 group-hover:text-yellow-900 transition-colors duration-200" />
                </span>
                {loading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing In...
                  </div>
                ) : (
                  'Sign In'
                )}
              </button>
            </div>
          </form>

          {/* Social Login Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/20"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white/10 backdrop-blur-lg text-white/60">Or continue with</span>
            </div>
          </div>
          
          {/* Google Login Button */}
          <button
            onClick={handleGoogleSignIn}
            disabled={googleLoading}
            className="group relative w-full flex justify-center py-3 px-4 border border-white/30 rounded-xl shadow-sm text-sm font-medium text-white bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:-translate-y-1"
          >
            <span className="absolute left-0 inset-y-0 flex items-center pl-3">
              <FaGoogle className="h-5 w-5 text-white/70 group-hover:text-white transition-colors duration-200" />
            </span>
            {googleLoading ? (
              <div className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Signing In with Google...
              </div>
            ) : (
              'Sign In with Google'
            )}
          </button>

          <div className="flex items-center justify-between">
            <div className="text-sm">
              <Link
                to="/forgot-password"
                className="font-medium text-yellow-400 hover:text-yellow-300 transition-colors duration-200 relative group"
              >
                <span>Forgot your password?</span>
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-yellow-400 group-hover:w-full transition-all duration-300"></span>
              </Link>
            </div>
            <div className="text-sm">
              <Link
                to="/register"
                className="font-bold bg-gradient-to-r from-yellow-400 to-yellow-500 text-black px-4 py-2 rounded-lg hover:from-yellow-500 hover:to-yellow-600 transition-all duration-200 transform hover:-translate-y-1 shadow-md"
              >
                Create Account
              </Link>
            </div>
          </div>
        </div>

        {/* Description Section */}
        <div className="text-center">
          <h3 className="text-xl font-bold text-white mb-2 relative inline-block">
            Language Learning Platform
            <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-gradient-to-r from-yellow-400 to-transparent rounded-full"></span>
          </h3>
          <p className="text-white/70 text-sm max-w-md mx-auto">
            Experience seamless language learning with interactive features. Learn, practice, and master new languages with confidence.
          </p>
        </div>

        {/* Footer with Privacy Policy and Terms of Service links */}
        <div className="text-center text-xs text-white/60 mt-4">
          <div className="flex justify-center space-x-4">
            <Link to="/privacy-policy" className="hover:text-white transition-colors duration-200">
              Privacy Policy
            </Link>
            <span>•</span>
            <Link to="/terms-of-service" className="hover:text-white transition-colors duration-200">
              Terms of Service
            </Link>
          </div>
          <p className="mt-2">© 2025 Amalie AI. All rights reserved.</p>
        </div>
      </div>

      {/* Existing Account Modal */}
      {showExistingAccountModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
          <div className="bg-white/10 backdrop-blur-xl rounded-xl shadow-xl max-w-md w-full p-6 border border-white/20">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-400/20 mb-4">
                <svg className="h-6 w-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-white mb-2">
                Account Already Exists
              </h3>
              <p className="text-sm text-white/70 mb-6">
                If you already have an account on any amah-llc site, you do not have to create a new account. Please log in with your existing account.
              </p>
              <div className="flex flex-col sm:flex-row justify-center space-y-2 sm:space-y-0 sm:space-x-4">
                <button
                  onClick={() => setShowExistingAccountModal(false)}
                  className="px-4 py-2 text-white/70 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <Link
                  to="/login"
                  className="px-6 py-2 bg-yellow-400 text-black rounded-lg hover:bg-yellow-500 transition-colors"
                >
                  Go to Login
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Styles */}
      <style jsx>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.2); }
        }
        
        @keyframes shooting-star {
          0% { 
            transform: translateX(-100px) translateY(100px) rotate(-45deg);
            opacity: 0;
          }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { 
            transform: translateX(300px) translateY(-300px) rotate(-45deg);
            opacity: 0;
          }
        }
        
        .animate-twinkle {
          animation: twinkle 3s ease-in-out infinite;
        }
        
        .animate-shooting-star {
          animation: shooting-star 3s linear infinite;
        }
      `}</style>
    </div>
  );
}